/**
 * useSupervisionQueue
 *
 * Filter and sort the supervision queue based on current filters.
 * In production, replace with useSearchResources for FHIR Task/DocumentReference.
 */

import { useMemo } from 'react';
import { getSupervisionQueueTypeLabel, type SupervisionFilters, type SupervisionQueueItem } from '../types';

export function useSupervisionQueue<T extends SupervisionQueueItem>(
  items: T[],
  filters: SupervisionFilters
): T[] {
  return useMemo(() => {
    let result = [...items] as T[];

    if (filters.clinicianIds.length > 0) {
      result = result.filter((i) => filters.clinicianIds.includes(i.clinicianId));
    }
    if (filters.documentTypes.length > 0) {
      result = result.filter((i) => filters.documentTypes.includes(i.documentType));
    }
    if (filters.status.length > 0) {
      result = result.filter((i) => filters.status.includes(i.status));
    }
    if (filters.riskLevel !== 'all') {
      result = result.filter((i) => i.riskLevel === filters.riskLevel);
    }
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      result = result.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.clinicianName.toLowerCase().includes(q) ||
          (i.clientName?.toLowerCase().includes(q) ?? false) ||
          getSupervisionQueueTypeLabel(i).toLowerCase().includes(q),
      );
    }

    const dir = filters.sortDirection === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      switch (filters.sortBy) {
        case 'oldest':
          return (
            (new Date(a.submittedAt ?? a.dueDate ?? 0).getTime() -
              new Date(b.submittedAt ?? b.dueDate ?? 0).getTime()) *
            dir
          );
        case 'newest':
          return (
            (new Date(b.submittedAt ?? b.dueDate ?? 0).getTime() -
              new Date(a.submittedAt ?? a.dueDate ?? 0).getTime()) *
            dir
          );
        case 'risk': {
          const distA = a.editDistanceFromPreviousNote ?? -1;
          const distB = b.editDistanceFromPreviousNote ?? -1;
          return (distA - distB) * dir;
        }
        case 'clinician':
          return a.clinicianName.localeCompare(b.clinicianName) * dir;
        case 'patient':
          return (
            (a.clientName ?? '').localeCompare(b.clientName ?? '', undefined, { sensitivity: 'base' }) * dir
          );
        case 'due':
          return (
            (new Date(a.dueDate ?? 0).getTime() - new Date(b.dueDate ?? 0).getTime()) * dir
          );
        default:
          return 0;
      }
    });

    return result;
  }, [items, filters]);
}
